/**
 * Same-origin calls to the storefront's own API routes (`/api/storefront/**`).
 *
 * <p>The storefront's route handlers add the tenant from the host and the
 * shopper's session server-side, so the browser never names a tenant and never
 * holds a token. Spatial's backend is reached through the generic extension
 * proxy, `/api/storefront/extensions/spatial/**`.
 */
export const API_ROOT = '/api/storefront';
export const SPATIAL_ROOT = `${API_ROOT}/extensions/spatial`;

/** A failed call, with the HTTP status so a block can tell "sign in" from "try again". */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

function defaultFetch(): Fetch {
  return (input, init) => fetch(input, init);
}

async function read<T>(response: Response): Promise<T> {
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!response.ok) {
    const b = body as { message?: string; error?: string; code?: string | number; errorKey?: string } | null;
    // The platform's error envelope prefixes the message with its key ("SPATIAL_DOOR_ORDER_INVALID:
    // Belgravia is not made…"). The key is for code, the sentence for the shopper.
    const key = b?.errorKey;
    const raw = b?.message || b?.error || `The shop answered ${response.status}.`;
    const message = key && raw.startsWith(`${key}: `) ? raw.slice(key.length + 2) : raw;
    throw new ApiError(response.status, message, key ?? (b?.code === undefined ? b?.error : String(b.code)));
  }
  return body as T;
}

export function http(fetchImpl: Fetch = defaultFetch()) {
  return {
    get: async <T>(path: string): Promise<T> =>
      read<T>(await fetchImpl(path, { headers: { accept: 'application/json' }, credentials: 'same-origin' })),
    send: async <T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> =>
      read<T>(
        await fetchImpl(path, {
          method,
          headers: { accept: 'application/json', ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
          credentials: 'same-origin',
          body: body === undefined ? undefined : JSON.stringify(body),
        }),
      ),
  };
}

export type Http = ReturnType<typeof http>;
